import type { ReactNode } from 'react';

interface MarqueeProps {
  children: ReactNode;
  className?: string;
  'aria-label'?: string;
  /** Percentage of the row's width spent fading each edge (bigger = stronger fade). */
  fadeSize?: number;
  /** Tailwind gap class for the space between items — per-instance because the
   *  design uses a different rhythm for the category chips and the offer tiles. */
  gapClassName?: string;
}

/** Infinite horizontal scroller: renders `children` twice back-to-back and
 *  animates a -50% translate so the loop is seamless. */
export function Marquee({
  children,
  className = '',
  fadeSize = 5,
  gapClassName = 'gap-3',
  ...rest
}: MarqueeProps) {
  // Inline style (not a Tailwind class) so it always wins regardless of
  // generated-CSS ordering — callers can safely tune the fade per instance.
  const maskImage = `linear-gradient(to right, transparent, black ${fadeSize}%, black ${100 - fadeSize}%, transparent)`;

  return (
    <div
      className={`group/marquee overflow-hidden ${className}`}
      style={{ maskImage, WebkitMaskImage: maskImage }}
      {...rest}
    >
      <div
        className={`animate-marquee flex w-max group-hover/marquee:[animation-play-state:paused] ${gapClassName}`}
      >
        <div className={`flex shrink-0 ${gapClassName}`}>{children}</div>
        <div className={`flex shrink-0 ${gapClassName}`} aria-hidden="true">
          {children}
        </div>
      </div>
    </div>
  );
}
